
import React, { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useReportTemplateList } from "./hooks/useReportTemplateList";
import { useTemplateFilters } from "./hooks/useTemplateFilters";
import { AIReportingChatWindow } from "./AIReportingChatWindow";
import { ReportTemplatesSidebar } from "./ReportTemplatesSidebar";
import { AIReportingChatActions } from './AIReportingChatActions';
import ErrorBoundary from "@/components/common/ErrorBoundary";
import { useAIReportingChat } from "./hooks/useAIReportingChat";
import { ReportTemplate } from "@/types/reportTypes";

const REPORT_CATEGORIES = [
  "All Categories",
  "Students", 
  "Parents",
  "Classes",
  "Staff",
  "Events",
  "Billing",
  "Apparel & Merchandise",
  "Communications",
];

const DEFAULT_TEMPLATES = [
  {
    id: 'student-attendance',
    name: 'Student Attendance Overview',
    description: 'Analyze student attendance patterns over time',
    prompt: 'Generate a report on student attendance rates over the past 3 months. Group by class and highlight any concerning trends.',
    category: 'Students'
  },
  {
    id: 'revenue-analysis',
    name: 'Revenue Breakdown',
    description: 'Detailed analysis of revenue sources',
    prompt: 'Create a comprehensive revenue report for this quarter broken down by class type, merchandise sales, and other income sources.',
    category: 'Billing'
  },
  {
    id: 'class-capacity',
    name: 'Class Capacity Utilization',
    description: 'Analyze how efficiently class capacities are being utilized',
    prompt: 'Generate a report on class capacity utilization. Identify classes that are consistently full and those that have low attendance.',
    category: 'Classes'
  },
  {
    id: 'parent-engagement',
    name: 'Parent Engagement Analysis',
    description: 'Measure parent engagement with the portal',
    prompt: 'Create a report analyzing parent engagement with our portal, including login frequency, feature usage, and communication responsiveness.',
    category: 'Parents'
  },
  {
    id: 'staff-performance',
    name: 'Staff Performance Metrics',
    description: 'Evaluate staff performance based on classes and student retention',
    prompt: 'Analyze staff performance metrics including class attendance rates, student retention, and parent satisfaction where available.',
    category: 'Staff'
  },
  {
    id: 'apparel-sales',
    name: 'Apparel Sales Analysis',
    description: 'Review apparel and merchandise sales performance',
    prompt: 'Generate a detailed report on apparel and merchandise sales, identifying top-selling items and revenue trends.',
    category: 'Apparel & Merchandise'
  },
  {
    id: 'communication-effectiveness',
    name: 'Communication Effectiveness',
    description: 'Analyze effectiveness of organizational communications',
    prompt: 'Create a report on our communication effectiveness, including message open rates, response times, and engagement metrics.',
    category: 'Communications'
  },
  {
    id: 'events-performance',
    name: 'Events Performance',
    description: 'Analyze performance and attendance of special events',
    prompt: 'Generate a report on our special events performance, including attendance rates, revenue generated, and participant feedback if available.',
    category: 'Events'
  },
];

export const AIReportingChat = () => {
  const [showTemplates, setShowTemplates] = useState(true);

  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;

  const {
    selectedCategories,
    setSelectedCategories,
    searchTerm,
    setSearchTerm,
    handleCategoryChange
  } = useTemplateFilters();

  // Restore the useReportTemplateList hook
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
    loadSavedReports
  } = useReportTemplateList(DEFAULT_TEMPLATES, organizationId, {
    selectedCategories,
    setSelectedCategories,
    searchTerm,
    setSearchTerm,
  });

  const {
    messages,
    input,
    setInput,
    isLoading,
    handleSendMessage,
    handleTemplateSelect,
    handleSaveReport,
    resetConversation
  } = useAIReportingChat();

  const onSaveReport = async () => {
    try {
      await handleSaveReport();
      await loadSavedReports();
    } catch (error) {
      console.error('Error in onSaveReport:', error);
    }
  };

  const onTemplateSelect = (template: ReportTemplate) => {
    handleTemplateSelect(template);
    setShowTemplates(false);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] bg-background">
      <ErrorBoundary componentName="AIReportingChatActions">
        <AIReportingChatActions
          showTemplates={showTemplates}
          setShowTemplates={setShowTemplates}
          handleSaveReport={onSaveReport}
          resetConversation={resetConversation}
          selectedCategory={selectedCategories.length === 1 ? selectedCategories[0] : "All Categories"}
          setSelectedCategory={(category) => handleCategoryChange(category, "All Categories")}
          reportCategories={REPORT_CATEGORIES}
        />
      </ErrorBoundary>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 flex-grow overflow-hidden">
        {showTemplates && (
          <ReportTemplatesSidebar
            templates={templates}
            loading={templatesLoading}
            error={templatesError}
            selectedCategories={selectedCategories}
            setSelectedCategories={setSelectedCategories}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            reportCategories={REPORT_CATEGORIES}
            onSelectTemplate={onTemplateSelect}
          />
        )}
        <div className={showTemplates ? "md:col-span-2 flex flex-col" : "md:col-span-3 flex flex-col"}>
          <AIReportingChatWindow
            messages={messages}
            isLoading={isLoading}
            input={input}
            setInput={setInput}
            handleSendMessage={handleSendMessage}
          />
        </div>
      </div>
    </div>
  );
};
